/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useCallback, useState } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  Icons,
  Text,
  THEME_CONSTANTS,
  useSnackbar,
} from '@web-stories-wp/design-system';
import { PAGE_WIDTH } from '@web-stories-wp/units';

/**
 * Internal dependencies
 */
import { useAnyResourceInLoadingState } from '@hooks';
import { useCmsApi } from '@hooks/api';
import { useTemplateSaveAssetTypes } from '@hooks/studioContext';
import { useLocalMedia } from '../../../../app';
import { useConfig } from '../../../../app/config';
import { useStory } from '../../../../app/story';
import { getStoryMarkup } from '../../../../output/index';
import isDefaultPage from '../../../../utils/isDefaultPage';
import { focusStyle } from '../../../panels/shared';
import Tooltip from '../../../tooltip';
import SaveTemplateDialog from './saveTemplateDialog';

const ButtonWrapper = styled.div`
  padding: 2px 20px 0;
`;

const StyledButton = styled.button`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.bg.tertiary};
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  gap: 20px;
  min-height: 63px;
  padding: 10px;
  width: 100%;

  &:hover {
    background-color: ${({ theme }) =>
      theme.colors.interactiveBg.secondaryHover};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.interactiveBg.disable};
    cursor: default;
  }

  ${focusStyle};
`;

const IconFrame = styled.span`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.bg.primary};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.fg.secondary};
  display: flex;
  flex: 0 0 32px;
  height: 43px;
  justify-content: center;
  width: 32px;
`;

const StyledPlusIcon = styled(Icons.PlusFilled)`
  height: 32px;
  width: 32px;
`;

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.colors.fg.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

function SaveCurrentPageAsTemplateButton({
  assetType,
  initialFolderId = '',
  onTemplateSaved,
}) {
  const { showSnackbar } = useSnackbar();
  const { studioTemplateApi } = useCmsApi();
  const { metadata } = useConfig();
  const { isUploading } = useLocalMedia((state) => ({
    isUploading: state.state.isUploading,
  }));
  const anyResourceInLoadingState = useAnyResourceInLoadingState();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { currentPage, story } = useStory(({ state }) => ({
    currentPage: state.currentPage,
    story: state.story,
  }));
  const saveAssetTypes = useTemplateSaveAssetTypes(assetType);

  const buttonLabel = __('Save current page as template', 'web-stories');
  const isPageEmpty = !currentPage || isDefaultPage(currentPage);
  const disabledTooltip = isPageEmpty
    ? __(
        'Add content to this page before saving it as a template.',
        'web-stories'
      )
    : '';
  const isDisabled =
    isPageEmpty ||
    isSaving ||
    isUploading ||
    anyResourceInLoadingState ||
    saveAssetTypes.length === 0;

  const handleSaveTemplate = useCallback(
    async ({ title, folderId }) => {
      setIsSaving(true);

      try {
        const pageData = {
          ...currentPage,
          canvasWidth: PAGE_WIDTH,
        };
        const storyMarkup = await getStoryMarkup(
          story,
          [currentPage],
          metadata
        );

        const response = await studioTemplateApi.save({
          jsonDefinition: JSON.stringify(pageData),
          ampHtml: storyMarkup,
          title,
          folderId,
          assetTypes: saveAssetTypes,
        });

        showSnackbar({
          message: 'Page saved as template.',
          dismissable: true,
        });

        setIsDialogOpen(false);
        await onTemplateSaved?.(response);
      } catch (error) {
        console.error('Failed to save template:', error);
        showSnackbar({
          message: 'Failed to save template. Please try again.',
          dismissable: true,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      currentPage,
      metadata,
      onTemplateSaved,
      saveAssetTypes,
      showSnackbar,
      story,
      studioTemplateApi,
    ]
  );

  return (
    <ButtonWrapper>
      <SaveTemplateDialog
        initialFolderId={initialFolderId}
        initialTitle={currentPage?.title || ''}
        isOpen={isDialogOpen}
        isSaving={isSaving}
        onClose={() => !isSaving && setIsDialogOpen(false)}
        onSave={handleSaveTemplate}
      />
      <Tooltip title={disabledTooltip} hasTail>
        <StyledButton
          aria-label={
            disabledTooltip ? `${buttonLabel}. ${disabledTooltip}` : buttonLabel
          }
          disabled={isDisabled}
          onClick={() => setIsDialogOpen(true)}
          type="button"
        >
          <IconFrame aria-hidden="true">
            <StyledPlusIcon />
          </IconFrame>
          <StyledText size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.MEDIUM}>
            {buttonLabel}
          </StyledText>
        </StyledButton>
      </Tooltip>
    </ButtonWrapper>
  );
}

SaveCurrentPageAsTemplateButton.propTypes = {
  assetType: PropTypes.string,
  initialFolderId: PropTypes.string,
  onTemplateSaved: PropTypes.func,
};

export default SaveCurrentPageAsTemplateButton;
